import { Typography, Button, Stack, IconButton, Tooltip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import {
  OpenInNew,
  Launch as LaunchIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material'
import { type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table'
import { ProjectModel, ProjectModelVersion } from '@/types/project-models'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useGetProjectLinkedModels } from '@/service-library/hooks/project-linked-models'
import { prettifyDate } from '@/utils/time'
import {
  getAccuracyColor,
  getCurrentVersion,
  getHighestVersion,
  getModelStatus,
  getModelVersionAccuracy,
  getSemanticVersion,
  getTrainingVersion,
} from '@/utils/model-page'
import ModelStatusLabel from './ModelStatusLabel'
import VersionTable from './VersionTable'

export type ModelGroup = {
  parentModel: ProjectModel
  childModels: ProjectModel[]
}

type ModelTableProps = { group: ModelGroup }

export default function ModelTable({ group }: ModelTableProps) {
  const [sorting, setSorting] = useLocalStorage<MRT_SortingState>(
    `models-table-${group.parentModel.id}-sorting`,
    [],
  )

  const navigate = useNavigate()
  const handleRowClick = (model: ProjectModel) => {
    const workflowId = model.training_workflow_id
    let url = `/projects/${model.project_id}/dashboard?project_model=${model.id}`
    if (workflowId) {
      url += `&workflow=${workflowId}`
    }
    navigate(url)
  }

  const columns: MRT_ColumnDef<ProjectModel>[] = [
    {
      id: 'go-to-dashboard',
      header: '', // No header text for a cleaner look
      size: 48, // Minimal size for an icon button
      enableSorting: false,
      Cell: ({ row }) => (
        <Tooltip title="Go to model dashboard">
          <IconButton
            onClick={(e) => {
              e.stopPropagation() // Prevent other row click events if any
              handleRowClick(row.original)
            }}
            aria-label="Go to dashboard"
            title="Go to dashboard"
            size="small"
          >
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      header: 'Name',
      accessorKey: 'name',
      size: 250,
      enableSorting: true,
    },
    {
      header: 'Current Version',
      accessorFn: (row) => {
        const currentVersion = getCurrentVersion(row.project_model_versions)
        if (!currentVersion) return ''
        return getSemanticVersion(currentVersion)
      },
      id: 'currentVersion',
      size: 120,
      enableSorting: false,
    },
    {
      header: 'Latest Version',
      accessorFn: (row) => getHighestVersion(row),
      id: 'latestVersionDetails',
      size: 120,
      enableSorting: false,
      Cell: ({ cell }) => {
        const highestVersion = cell.getValue<ProjectModelVersion | null>()

        if (!highestVersion) return ''
        const versionString = getSemanticVersion(highestVersion)
        if (highestVersion.training_status === 'error') {
          return (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title="Training failed">
                <ErrorOutlineIcon color="error" sx={{ fontSize: '1rem' }} />
              </Tooltip>
              <Typography color="error.main">{versionString}</Typography>
            </Stack>
          )
        }
        return versionString
      },
    },
    {
      header: 'Training Completed',
      accessorFn: (row) => {
        if (getTrainingVersion(row.project_model_versions)) return ''
        const currentVersion = getCurrentVersion(row.project_model_versions)
        return currentVersion
          ? prettifyDate(currentVersion.training_completed_at)
          : ''
      },
      id: 'lastTrainingFinished',
      size: 200,
    },
    {
      header: 'Accuracy',
      accessorFn: (row) => {
        if (getTrainingVersion(row.project_model_versions)) return ''
        const currentVersion = getCurrentVersion(row.project_model_versions)
        return currentVersion ? getModelVersionAccuracy(currentVersion) : ''
      },
      id: 'accuracy',
      size: 150,
      Cell: ({ cell }) => {
        const accuracy = cell.getValue<string>()
        const color = getAccuracyColor(accuracy)
        return <Typography color={color}>{accuracy}</Typography>
      },
    },
    {
      header: 'Status',
      accessorFn: (row) => getModelStatus(row),
      id: 'status',
      size: 150,
      enableSorting: true,
      Cell: ({ renderedCellValue }) => {
        const status =
          renderedCellValue as ProjectModelVersion['training_status']
        return <ModelStatusLabel status={status} />
      },
    },
  ]

  const tableData = [group.parentModel, ...group.childModels]

  const projectId = group.parentModel.project_id

  const { projectLinkedModels } = useGetProjectLinkedModels({
    filters: {
      project_id: projectId,
    },
  })
  const projectLinkedModel = projectLinkedModels?.find(
    (model) => model.project_model_id === group.parentModel.id,
  )

  // Set up the table
  const table = useZerapixTable({
    columns,
    data: tableData,
    title: group.parentModel.name,
    enablePagination: false,
    enableSorting: true,
    enableExpandAll: false, // Hide expand all/collapse all button
    enableColumnResizing: true,
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: '', // Hide the header for the expand column
        size: 20,
      },
    },
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    renderDetailPanel: ({ row }) => {
      if (
        !row.original.project_model_versions ||
        row.original.project_model_versions.length === 0
      ) {
        return <Typography>No versions available for this model.</Typography>
      }
      return <VersionTable model={row.original} />
    },
    renderTableActions: () =>
      projectLinkedModel ? (
        <Button
          variant="text"
          startIcon={<OpenInNew sx={{ fontSize: 10, p: 0 }} />}
          onClick={() =>
            navigate(
              `/projects/${projectId}/settings/model-library/${projectLinkedModel?.id}`,
            )
          }
        >
          View in Model Library
        </Button>
      ) : null,
  })

  return <ZerapixTable<ProjectModel> table={table} />
}
